import { usePrevious } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { ArrowRecurringIcon, BlockIcon, CalendarDateIcon } from '@calendly/ui/components/icons';
import { useTooltip } from '@calendly/ui/components/tooltip';
import classNames from 'classnames';
import { useLayoutEffect, useRef, forwardRef } from 'react';
import Intervals from './intervals';
const ICON_FOR_KIND = {
  holiday: BlockIcon,
  date_specific: CalendarDateIcon,
  weekly: ArrowRecurringIcon
};
const daySelectableCss = "d8jub8k";
const daySelectedCss = "d1fud05c";
const daySelectionModeCss = "d114r59w";
const dayActiveCss = "d1jlg9wr";
const dayKindHolidayCss = "d47tnwr";
const dayKindDateSpecificCss = "djp6szw";
const dayCss = "dlnetwf";
const Day = forwardRef((props, ref) => {
  const {
    selectable,
    selected,
    selectionMode,
    active,
    kind,
    ...restProps
  } = props;
  return <BareButton ref={ref} {...restProps} className={classNames(dayCss, {
    [daySelectableCss]: selectable,
    [daySelectedCss]: selected,
    [daySelectionModeCss]: selectionMode,
    [dayActiveCss]: active,
    [dayKindHolidayCss]: kind?.toString() === 'holiday',
    [dayKindDateSpecificCss]: kind?.toString() === 'date_specific'
  }, props.className)} />;
});
const dateSelectedCss = "d4m54ph";
const dateActiveCss = "d15v4gis";
const dateCss = "d11obypm";
const Date = forwardRef((props, ref) => {
  const {
    selected,
    active,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(dateCss, {
    [dateSelectedCss]: selected,
    [dateActiveCss]: active
  }, props.className)} />;
});
const wrapperCss = "w1p583k2";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
const iconWithTooltipCss = "iaylvli";
const IconWithTooltip = forwardRef(({
  Icon,
  Tooltip,
  className,
  ...props
}, ref) => <Wrapper ref={ref} {...props} data-component="cell-tooltip" className={classNames(iconWithTooltipCss, className)}>
    <Icon />
    <Tooltip />
  </Wrapper>);
const rightArrowCss = "r70fn0n";
const RightArrow = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(rightArrowCss, props.className)} />;
});
const leftArrowCss = "l14apwla";
const LeftArrow = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(leftArrowCss, props.className)} />;
});
const getAvailabilityKind = (date, holiday) => {
  let kind = 'weekly';
  if (holiday && holiday.enabled) {
    kind = 'holiday';
  } else if (date) {
    kind = 'date_specific';
  }
  return kind;
};

// i18n-tasks-use t('publisher.availability.schedules.tooltip.weekly')
// i18n-tasks-use t('publisher.availability.schedules.tooltip.holiday')
// i18n-tasks-use t('publisher.availability.schedules.tooltip.date_specific')
export const DayWithIcon = ({
  id,
  wday,
  datesMap,
  holidaysMap,
  days,
  selectedDays,
  isBeforeToday,
  isSelecting,
  selectDate,
  onSelectionStart,
  onSelectionStop,
  onSelectionMove,
  text,
  isToday,
  activeWeekends,
  inActiveRange,
  isDropdownActive,
  reduceToFit,
  className
}) => {
  const ref = useRef();
  const isWeekend = wday === 'saturday' || wday === 'sunday';
  const inRange = !isBeforeToday && inActiveRange(id) && (activeWeekends || !isWeekend);
  const shouldShowArrows = _id => isSelecting && selectedDays.length === 1 && selectedDays.includes(_id);
  const availabilityKind = getAvailabilityKind(datesMap[id], holidaysMap[id]);
  const intervals = datesMap[id] ? datesMap[id].intervals : days[wday];
  const tooltipText = I18n.t(`publisher.availability.schedules.tooltip.${availabilityKind}`);
  const [Tooltip, {
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    ...tooltipHostProps
  }] = useTooltip(tooltipText);
  const wasDropdownActive = usePrevious(isDropdownActive);
  useLayoutEffect(() => {
    if (wasDropdownActive && !isDropdownActive) {
      ref.current.focus();
    }
  }, [isDropdownActive, wasDropdownActive]);
  return <Day className={className} ref={ref} id={id} selectionMode={isSelecting} selected={selectedDays.includes(id)} selectable={!isBeforeToday} active={inRange} data-active={inRange.toString()} aria-current={isToday ? 'date' : null} kind={availabilityKind} onFocus={() => {
    if (!isSelecting && !isBeforeToday) onFocus();
  }} onBlur={onBlur} onMouseEnter={() => onSelectionMove(id, isBeforeToday)} onMouseDown={() => onSelectionStart(id, isBeforeToday)} onMouseUp={onSelectionStop} onClick={() => selectDate(id, isBeforeToday)}>
      {shouldShowArrows(id) && <RightArrow />}
      {shouldShowArrows(id) && <LeftArrow />}
      <Date selected={isToday} active={!isBeforeToday}>{text}</Date>
      {(!!datesMap[id] || !!holidaysMap[id] || days[wday].length > 0) && <>
          <IconWithTooltip Icon={ICON_FOR_KIND[availabilityKind]} Tooltip={Tooltip} kind={availabilityKind} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} active={inRange} role="button" aria-hidden={isBeforeToday} {...tooltipHostProps} />
          <Intervals holiday={holidaysMap[id]} intervals={intervals} inactive={!inRange} reduceToFit={reduceToFit} />
        </>}
    </Day>;
};

require("./day_with_icons.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./day_with_icons.js");